import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Button } from '@ui/button.tsx';
import { SelectDropdown, type SelectOption } from '@ui/select-dropdown.tsx';
import { Textarea } from '@ui/textarea.tsx';
import { ComboboxInput } from '@ui/combobox-input.tsx';
import { DatePicker } from '@ui/date-picker.tsx';
import { Calendar } from '@ui/calendar.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@ui/popover.tsx';
import { z } from 'zod';
import { useForm, useFormContext } from 'react-hook-form';
import { apiAdminpointsPostBody } from '@r/api/generated-zod/index.ts';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { CalendarIcon, Plus } from 'lucide-react';
import { useDebounce } from '@r/hooks/use-debounce.ts';
import type { Option } from '@r/types/index.ts';
import { type FC, useState, useMemo, useEffect } from 'react';
import {
  useApiAdmincompaniesGetCollection,
  useApiAdmincompaniesIdGet,
  useApiAdminusersGetCollection,
  useApiAdminpointsPost
} from '@r/api/generated/client.ts';
import type { CompanyCompanyCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import {
  LogicLogicReadModelType,
  PointPointImportModelTypeLabel,
  type UserUserCollectionReadModel
} from '@r/api/generated/client.schemas.ts';
import { useUser } from '@r/api/auth.tsx';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { toast } from 'sonner';
import { cn } from '@r/utils/utils.ts';
import { format } from 'date-fns';
import { toUtcEndOfDay, toUtcStartOfDay } from '@r/utils/date-utils.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

interface AddPointFormProps {
  onSuccess: () => void;
  fixedCompany?: { id: number; uniqueCode: string; name: string };
}

/** Convertit la saisie du montant en entier exploitable par le schéma du formulaire. */
const parseIntegerInput = (value: unknown): number | undefined => {
  if (typeof value === 'number') return Number.isInteger(value) ? value : Math.floor(value);
  if (typeof value !== 'string') return undefined;
  const normalized = value.replace(',', '.').trim();
  if (normalized.length === 0) return undefined;
  const parsed = Number.parseInt(normalized, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const typeLabelOptions = Object.values(PointPointImportModelTypeLabel).map((value) => ({
  value,
  label: value
}));

const expirationDisabledTypeLabels: ReadonlySet<PointPointImportModelTypeLabel> = new Set([
  PointPointImportModelTypeLabel.SPENT,
  PointPointImportModelTypeLabel.EXPIRED,
  PointPointImportModelTypeLabel.CANCELED
]);

/** Indique si un objectif peut être associé aux points (annulation sur une logique à target en points). */
const isTargetSelectable = (
  isTargetPointLogic: boolean,
  typeLabel: PointPointImportModelTypeLabel
) => isTargetPointLogic && typeLabel === PointPointImportModelTypeLabel.CANCELED;

/** Adapte le schéma API aux types manipulés par les champs React du formulaire. */
const getAddPointSchema = (t: ReturnType<typeof useTranslation>['t']) => {
  const { shape } = apiAdminpointsPostBody;

  return apiAdminpointsPostBody.extend({
    companyUniqueCode: z.union([
      z.string().min(1, t('admin.points.add.form.company.required')),
      z.literal(null)
    ]),
    userUniqueCode: shape.userUniqueCode.describe(''),
    amount: z.preprocess(
      parseIntegerInput,
      z.number({ required_error: t('admin.points.add.form.amount.required') })
    ),
    typeLabel: shape.typeLabel.describe(''),
    valueDate: z.date({ required_error: t('admin.points.add.form.value.date.required') }),
    expirationDate: z.date().nullable().optional(),
    label: shape.label.describe(''),
    targetReference: shape.targetReference.describe('')
  });
};

interface PointTargetReferenceFieldProps {
  companyId: number | null;
}

/** Sélection de l'objectif impacté par une annulation de points, parmi ceux de l'entreprise. */
const PointTargetReferenceField: FC<PointTargetReferenceFieldProps> = ({ companyId }) => {
  const { t } = useTranslation();
  const { control } = useFormContext<{ targetReference?: string | null }>();

  const companyDetailQuery = useApiAdmincompaniesIdGet(
    companyId != null ? String(companyId) : null,
    { query: { enabled: companyId != null } }
  );

  const targetSelectOptions: SelectOption[] = useMemo(
    () =>
      (companyDetailQuery.data?.targetStatuses ?? [])
        .map((status) => status.logicTypeTarget?.reference)
        .filter((reference): reference is string => Boolean(reference))
        .map((reference) => ({ value: reference, label: reference })),
    [companyDetailQuery.data?.targetStatuses]
  );

  const hasNoTarget =
    companyId != null && !companyDetailQuery.isLoading && targetSelectOptions.length === 0;

  return (
    <FormField
      control={control}
      name="targetReference"
      render={({ field }) => (
        <FormItem>
          <FormLabel>{t('admin.points.add.form.target.reference')}</FormLabel>
          <FormDescription>
            {t('admin.points.add.form.target.reference.description')}
          </FormDescription>
          <FormControl>
            <SelectDropdown
              key={companyId ?? 'no-company'}
              options={targetSelectOptions}
              loading={companyDetailQuery.isLoading}
              disabled={companyId == null || targetSelectOptions.length === 0}
              placeholder={t('admin.points.add.form.target.reference.placeholder')}
              defaultValue={field.value ?? undefined}
              allowDeselect={true}
              onChange={(option) => {
                field.onChange(option?.value ?? undefined);
              }}
            />
          </FormControl>
          <FormMessage />
          {hasNoTarget ? (
            <p className="text-xs text-muted-foreground">
              {t('admin.points.add.form.target.reference.empty')}
            </p>
          ) : null}
        </FormItem>
      )}
    />
  );
};

/** Formulaire de création manuelle d'un mouvement de points. */
export const AddPointForm: FC<AddPointFormProps> = ({ onSuccess, fixedCompany }) => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const [companyQuery, setCompanyQuery] = useState('');
  const debouncedCompanyQuery = useDebounce(companyQuery, 500);
  const [selectedCompanyOption, setSelectedCompanyOption] = useState<Option | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const isCompanyFixed = Boolean(fixedCompany);
  const user = useUser();
  const { workspace } = UseWorkspace(user.data);
  const isTargetPointLogic =
    workspace?.currentLogicWorkspace?.logic.type === LogicLogicReadModelType.TARGET_POINT;

  const companiesQuery = useApiAdmincompaniesGetCollection(
    !isCompanyFixed && debouncedCompanyQuery && debouncedCompanyQuery.length > 0
      ? { search: debouncedCompanyQuery, perPage: 50 }
      : { 'order[name]': 'asc' as const }
  );

  const companyOptions: Option[] = useMemo(
    () =>
      fixedCompany
        ? [{ label: fixedCompany.name, value: fixedCompany.uniqueCode }]
        : (companiesQuery.data?.data ?? []).map((company: CompanyCompanyCollectionReadModel) => ({
            label: company.name,
            value: company.uniqueCode
          })),
    [companiesQuery.data?.data, fixedCompany]
  );

  const selectedCompanyName = selectedCompanyOption?.label ?? '';

  const usersQuery = useApiAdminusersGetCollection(
    selectedCompanyName
      ? {
          'userWorkspaces.company.name': selectedCompanyName,
          'maxRole[]': ['ROLE_MEMBER', 'ROLE_MANAGER'] as const,
          perPage: 500
        }
      : undefined,
    { query: { enabled: selectedCompanyName.length > 0 } }
  );

  const beneficiaryOptions: Option[] = useMemo(() => {
    const users = (usersQuery.data?.data ?? []) as UserUserCollectionReadModel[];
    return users.map((u) => {
      const uw = u.userWorkspaces?.find(
        (w) => w.company?.uniqueCode === selectedCompanyOption?.value
      );
      const uniqueCode = uw?.uniqueCode ?? u.userWorkspaces?.[0]?.uniqueCode ?? '';
      return { label: u.fullname, value: uniqueCode };
    });
  }, [usersQuery.data?.data, selectedCompanyOption?.value]);

  const beneficiarySelectOptions: SelectOption[] = useMemo(
    () =>
      beneficiaryOptions.map((opt) => ({
        value: opt.value,
        label: opt.label
      })),
    [beneficiaryOptions]
  );

  const typeLabelSelectOptions: SelectOption[] = useMemo(
    () =>
      typeLabelOptions.map((opt) => ({
        value: opt.value,
        label: opt.label
      })),
    []
  );

  const formSchema = getAddPointSchema(t);

  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      companyUniqueCode: '',
      userUniqueCode: null,
      amount: undefined,
      typeLabel: 'CORE' as const,
      valueDate: undefined,
      expirationDate: null,
      label: '',
      targetReference: undefined
    }
  });

  const selectedTypeLabel = form.watch('typeLabel');
  const isExpirationDisabled = expirationDisabledTypeLabels.has(selectedTypeLabel);
  const shouldSelectTarget = isTargetSelectable(isTargetPointLogic, selectedTypeLabel);

  useEffect(() => {
    if (isExpirationDisabled) {
      form.setValue('expirationDate', null);
    }
  }, [form, isExpirationDisabled]);

  useEffect(() => {
    if (!shouldSelectTarget) {
      form.setValue('targetReference', undefined);
    }
  }, [form, shouldSelectTarget]);

  useEffect(() => {
    if (!fixedCompany) return;
    const option = { label: fixedCompany.name, value: fixedCompany.uniqueCode };
    setSelectedCompanyOption(option);
    setSelectedCompanyId(fixedCompany.id);
    setCompanyQuery(fixedCompany.name);
    form.setValue('companyUniqueCode', fixedCompany.uniqueCode);
    form.setValue('userUniqueCode', null);
    form.setValue('targetReference', undefined);
  }, [fixedCompany, form]);

  const addPointMutation = useApiAdminpointsPost({
    mutation: {
      onSuccess: () => {
        toast.success(t('admin.points.add.success'));
        form.reset({
          companyUniqueCode: isCompanyFixed ? (fixedCompany?.uniqueCode ?? '') : '',
          userUniqueCode: null,
          amount: undefined,
          typeLabel: 'CORE',
          valueDate: undefined,
          expirationDate: null,
          label: '',
          targetReference: undefined
        });
        if (!isCompanyFixed) {
          setSelectedCompanyOption(null);
          setSelectedCompanyId(null);
        }
        onSuccess();
      },
      onError: (error) => {
        showOrvalErrorToast(error, t('toast.error.label'));
      }
    }
  });

  function onSubmit(data: z.infer<typeof formSchema>) {
    addPointMutation.mutate({
      data: {
        companyUniqueCode: data.companyUniqueCode?.trim() || null,
        userUniqueCode: data.userUniqueCode ?? undefined,
        amount: data.amount,
        typeLabel: data.typeLabel,
        valueDate: toUtcStartOfDay(data.valueDate).toISOString(),
        expirationDate:
          !isExpirationDisabled && data.expirationDate
            ? toUtcEndOfDay(data.expirationDate).toISOString()
            : null,
        label: data.label ?? undefined,
        targetReference: shouldSelectTarget ? (data.targetReference ?? undefined) : undefined
      }
    });
  }

  return (
    <Form {...form} schema={formSchema}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="companyUniqueCode"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('admin.points.add.form.company')}</FormLabel>
              <FormControl>
                <div className={isCompanyFixed ? 'pointer-events-none' : undefined}>
                  <ComboboxInput
                    placeholder={t('admin.points.add.form.company.placeholder')}
                    immediate={true}
                    options={companyOptions}
                    input={companyQuery}
                    onInputChange={setCompanyQuery}
                    value={selectedCompanyOption}
                    onValueChange={(option) => {
                      if (isCompanyFixed) return;
                      setSelectedCompanyOption(option);
                      const companyDataList = (companiesQuery.data?.data ??
                        []) as CompanyCompanyCollectionReadModel[];
                      setSelectedCompanyId(
                        option
                          ? (companyDataList.find((c) => c.uniqueCode === option.value)?.id ?? null)
                          : null
                      );
                      field.onChange(option?.value ?? '');
                      form.setValue('userUniqueCode', null);
                      form.setValue('targetReference', undefined);
                    }}
                    loading={companiesQuery.isLoading}
                    className={fieldState.error ? 'border-destructive' : undefined}
                    disabled={isCompanyFixed}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="userUniqueCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('admin.points.add.form.beneficiary')}</FormLabel>
              <FormControl>
                <SelectDropdown
                  options={beneficiarySelectOptions}
                  loading={usersQuery.isLoading}
                  disabled={!selectedCompanyName}
                  placeholder={t('admin.points.add.form.beneficiary.placeholder')}
                  defaultValue={field.value ?? undefined}
                  allowDeselect={true}
                  onChange={(option) => {
                    field.onChange(option?.value ?? null);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="typeLabel"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('admin.points.add.form.type')}</FormLabel>
              <FormControl>
                <SelectDropdown
                  options={typeLabelSelectOptions}
                  loading={false}
                  placeholder={t('admin.points.add.form.type.placeholder')}
                  defaultValue={field.value}
                  allowDeselect={false}
                  onChange={(option) => {
                    field.onChange((option?.value ?? 'CORE') as PointPointImportModelTypeLabel);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {shouldSelectTarget && <PointTargetReferenceField companyId={selectedCompanyId} />}

        <FormField
          control={form.control}
          name="amount"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('admin.points.add.form.amount')}</FormLabel>
              <FormControl>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={
                    typeof field.value === 'string' || typeof field.value === 'number'
                      ? field.value
                      : ''
                  }
                  onChange={(event) => field.onChange(event.target.value)}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="valueDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('admin.points.add.form.value.date')}</FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      className={cn(
                        'pl-3 text-left font-normal',
                        !field.value && 'text-muted-foreground'
                      )}
                    >
                      {field.value ? (
                        format(field.value, 'PPP')
                      ) : (
                        <span>{t('admin.points.add.form.value.date.pick')}</span>
                      )}
                      <CalendarIcon className="ml-auto w-4 h-4 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-auto" align="start">
                  <Calendar
                    mode="single"
                    selected={field.value ?? undefined}
                    onSelect={field.onChange}
                    disabled={(date) => date > new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="expirationDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('admin.points.add.form.expiration.date')}</FormLabel>
              <FormDescription>
                {t('admin.points.add.form.expiration.date.description')}
              </FormDescription>
              <FormControl>
                <DatePicker
                  value={field.value}
                  onChange={field.onChange}
                  disabled={isExpirationDisabled}
                  placeholder={t('admin.points.add.form.expiration.date.pick')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="label"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('admin.points.add.form.label')}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t('admin.points.add.form.label.placeholder')}
                  rows={3}
                  className="resize-none"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  error={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          rounded="full"
          loading={addPointMutation.isPending}
          disabled={!form.formState.isValid}
          className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
        >
          <Plus className="mr-2 w-4 h-4" />
          {t('common.add.point')}
        </Button>
      </form>
    </Form>
  );
};
